import { connect } from 'react-redux';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { parse } from '../../util/urlHelpers';
import { getBlogPageContent, saveBlogPageContent } from '../../util/api';
import { LayoutSingleColumn, Page, Modal, PrimaryButton, SecondaryButton } from '../../components';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import FooterComponent from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { stripHtml } from './blogHtml';

// Import all templates
import Template1Editorial from './templates/Template1Editorial';
import Template2BoldSplit from './templates/Template2BoldSplit';
import Template3Minimal from './templates/Template3Minimal';

// Import the original template component (we'll need to extract it)
import FortehBlogPageOriginal from './FortehBlogPage';

import css from './FortehBlogPage.module.css';

// Template configurations
const templateConfigs = {
  original: {
    component: FortehBlogPageOriginal,
    name: 'Original Forteh Blog',
    initialData: {
      hero: {
        label: 'The Forteh Blog',
        title: 'Your Blog Post Title Here',
        subtext: 'Your engaging subtitle that draws readers in.',
        meta: ['📍 Published by Forteh', '⏱ 5 min read', '🇨🇦 Canada'],
      },
      steps: [],
      roles: [],
      tableRows: [],
      stats: [],
      article: {},
      cta: {
        title: 'Ready to take the next step?',
        sub: 'Browse talent or explore more insights at forteh.ca',
        btn1: 'Hire Fractional Talent',
        btn2: 'Join as a Professional',
      },
    }
  },
  template1: {
    component: Template1Editorial,
    name: 'Editorial Template',
    initialData: {
      badge: 'Forteh — Blog Category',
      title: 'Your Blog Post Headline Goes Right Here',
      readTime: '4 min read',
      topicTag: 'Topic Tag',
      website: 'forteh.ca',
      intro: 'Your opening paragraph sets the tone — this is where you hook the reader.',
      sections: [
        {
          tag: '01',
          label: 'Section Label',
          title: 'Your Section Heading Here',
          content: 'Body copy sits here — conversational, clear, and never longer than it needs to be.'
        }
      ],
      quote: 'Your most compelling point, stated boldly.',
      ctaTitle: 'Ready to take the next step?',
      ctaSubtext: 'Browse talent or explore more insights at forteh.ca',
      ctaButton: 'Browse Talent →',
      ctaButtonUrl: 'https://forteh-development-546ec4c7069f.herokuapp.com'
    }
  },
  template2: {
    component: Template2BoldSplit,
    name: 'Bold Split Template',
    initialData: {
      badge: 'Forteh — Blog Category',
      title: 'Your Bold Blog Headline Lives Here',
      readTime: '4 min read',
      topicTag: 'Topic Tag',
      website: 'forteh.ca',
      statNumber: '60–80%',
      statLabel: 'Lead stat or key proof point for your post',
      intro: 'Your opening paragraph goes here.',
      cards: [
        {
          num: '01',
          title: 'Section One Title',
          text: 'Your point here, stated cleanly.'
        }
      ],
      quote: 'Your most powerful line from the post.',
      ctaTitle: 'Ready to take the next step?',
      ctaSubtext: 'Browse talent or explore more insights at forteh.ca',
      ctaButton: 'Browse Talent →',
      ctaButtonUrl: 'https://forteh-development-546ec4c7069f.herokuapp.com'
    }
  },
  template3: {
    component: Template3Minimal,
    name: 'Minimal Template',
    initialData: {
      badge: 'Forteh — Blog Category',
      title: 'A Clean, Elegant Headline for Thoughtful Readers',
      subtitle: 'A single sentence subtitle that gives context.',
      readTime: '4 min read',
      topicTag: 'Topic Tag',
      intro: 'Your opening paragraph here — this template suits thoughtful, long-form content.',
      rows: [
        {
          num: 1,
          title: 'First Point or Section Title',
          text: 'Body copy goes here.'
        }
      ],
      quote: 'A quieter pull quote style — elegant and understated.',
      ctaTitle: 'Ready to take the next step?',
      ctaSubtext: 'Browse talent or explore more insights at forteh.ca',
      ctaButton: 'Browse Talent →',
      ctaButtonUrl: 'https://forteh-development-546ec4c7069f.herokuapp.com'
    }
  }
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => (
  <Modal
    id="FortehBlogDeleteConfirmation"
    isOpen={isOpen}
    onClose={onClose}
    onManageDisableScrolling={() => { }}
    usePortal
  >
    <div className={css.modalContent}>
      <h3 className={css.modalTitle}>{title || 'Are you sure?'}</h3>
      <p className={css.modalMessage}>{message || 'This action cannot be undone.'}</p>
      <div className={css.modalActions}>
        <SecondaryButton onClick={onClose} className={css.modalBtn}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className={classNames(css.modalBtn, css.modalConfirmBtn)}
        >
          Confirm Delete
        </PrimaryButton>
      </div>
    </div>
  </Modal>
);

const FortehBlogPageNewComponent = (props) => {
  const {
    scrollingDisabled,
    currentUser,
    onManageDisableScrolling,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const { slug } = params;
  const config = useConfiguration();
  const query = parse(location.search);
  const isEditModeParam = !!query.edit;
  const templateParam = query.template || 'original';

  const isAdmin = currentUser?.attributes?.profile?.privateData?.admin;

  const [templateId, setTemplateId] = useState(templateParam);
  const [pageData, setPageData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(isEditModeParam);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('published');

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  const triggerConfirm = (title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      if (isAdmin && slug) {
        // Try to fetch existing content
        try {
          const res = await getBlogPageContent({ status: 'draft', slug });
          if (res.success && res.data?.pageData) {
            setPageData(res.data.pageData);
            setTemplateId(res.data.templateId || 'original');
            setCurrentStatus('draft');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.log('No draft found, checking published...');
        }

        try {
          const res = await getBlogPageContent({ status: 'published', slug });
          if (res.success && res.data?.pageData) {
            setPageData(res.data.pageData);
            setTemplateId(res.data.templateId || 'original');
            setCurrentStatus('published');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.log('No published content found');
        }
      }

      // Check for template selection from sessionStorage
      const selectedTemplate = sessionStorage.getItem('selectedTemplate');
      if (selectedTemplate && !slug) {
        const template = JSON.parse(selectedTemplate);
        setTemplateId(template.id);
        setPageData(template.data);
        setCurrentStatus('new');
        sessionStorage.removeItem('selectedTemplate'); // Clean up
      } else {
        // Use default template data
        const config = templateConfigs[templateId] || templateConfigs.original;
        setPageData(config.initialData);
        setCurrentStatus(slug ? 'not_found' : 'new');
      }
    } catch (err) {
      console.error('Failed to fetch blog content:', err);
      const config = templateConfigs[templateId] || templateConfigs.original;
      setPageData(config.initialData);
      setCurrentStatus('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [isAdmin, slug, templateParam]);

  const handleUpdate = (key, value) => {
    setPageData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async (status = 'draft') => {
    setSaving(true);
    try {
      const res = await saveBlogPageContent({ 
        pageData, 
        status, 
        slug,
        templateId 
      });
      
      if (res.success) {
        toast.success(`Content saved as ${status}!`);
        history.push('/everything-fractional');
      }
    } catch (err) {
      toast.error('Failed to save content.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      const res = await saveBlogPageContent({ 
        pageData, 
        status: 'published', 
        slug,
        templateId 
      });

      if (res.success) {
        toast.success('Live content updated successfully!');
        history.push('/everything-fractional');
      }
    } catch (err) {
      toast.error('Failed to update live content.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn
          topbar={
            <TopbarContainer
              className={css.topbar}
              mobileRootClassName={css.mobileTopbar}
              desktopClassName={css.desktopTopbar}
              currentPage="FortehBlogPage"
            />
          }
          footer={<FooterComponent />}
          className={css.pageRoot}
        >
          <div className={css.loadingOverlay}>
            <div className={css.loadingContent}>
              <div className={css.spinner}></div>
              <div className={css.loadingText}>Loading Blog Content</div>
              <div className={css.loadingSubtext}>Just a moment...</div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  // Get the template configuration
  const templateConfig = templateConfigs[templateId] || templateConfigs.original;
  const TemplateComponent = templateConfig.component;

  // For the original template, we need to render it differently
  if (templateId === 'original') {
    return (
      <FortehBlogPageOriginal 
        {...props}
        initialData={pageData}
        templateId={templateId}
      />
    );
  }

  // Dynamic SEO for non-original templates (template1/2/3 store title at top level)
  const blogTitle = stripHtml(pageData?.title);
  const blogDescription = stripHtml(pageData?.intro || pageData?.subtitle);
  const blogImage = pageData?.image;
  const pageTitle = blogTitle || `${templateConfig.name} - Forteh Blog`;
  const pageDescription = blogDescription
    || 'Insights, tips, and trends about fractional work and the future of careers from Forteh.';
  const socialImage = blogImage || config.branding.facebookImage;
  const twitterImage = blogImage || config.branding.twitterImage;
  const canonicalUrl = `${config.marketplaceRootURL}/forteh-blog/${slug || ''}`;

  const articleSchema = blogTitle ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blogTitle,
    description: pageDescription,
    image: socialImage ? [socialImage] : [],
    author: { '@type': 'Person', name: 'Raman', jobTitle: 'Founder of Forteh' },
    publisher: {
      '@type': 'Organization',
      name: config.marketplaceName,
      logo: { '@type': 'ImageObject', url: config.branding.logoImageMobileURL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    url: canonicalUrl,
  } : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: config.marketplaceRootURL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${config.marketplaceRootURL}/everything-fractional` },
      { '@type': 'ListItem', position: 3, name: blogTitle || 'Blog Post', item: canonicalUrl },
    ],
  };

  const schema = [breadcrumbSchema, ...(articleSchema ? [articleSchema] : [])];

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
      openGraphType="article"
      schema={schema}
      facebookImages={[{ width: 1200, height: 630, url: socialImage }]}
      twitterImages={[{ width: 600, height: 314, url: twitterImage }]}
      socialSharing={{
        title: pageTitle,
        description: pageDescription,
        images1200: [{ width: 1200, height: 630, url: socialImage }],
        images600: [{ width: 600, height: 314, url: twitterImage }],
      }}
    >
      <LayoutSingleColumn
        topbar={
          <TopbarContainer
            className={css.topbar}
            mobileRootClassName={css.mobileTopbar}
            desktopClassName={css.desktopTopbar}
            currentPage="FortehBlogPage"
          />
        }
        footer={<FooterComponent />}
        className={css.pageRoot}
      >
        <div className={css.root}>
          <TemplateComponent
            data={pageData}
            onChange={handleUpdate}
            isEditMode={isEditMode}
            triggerConfirm={triggerConfirm}
          />

          {/* Admin Controls */}
          {isAdmin && (
            <div className={css.adminControls}>
              <div className={css.statusIndicator}>
                Template: <strong>{templateConfig.name}</strong><br/>
                Status: <strong>{currentStatus?.toUpperCase()}</strong>
              </div>
              <button
                className={classNames(css.adminBtn, css.editToggleBtn)}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? 'Preview Mode' : 'Edit Mode'}
              </button>
              {currentStatus !== "published" && (
                <button
                  className={classNames(css.adminBtn, css.saveBtn)}
                  onClick={() => handleSave('draft')}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
              )}
              <button
                className={classNames(css.adminBtn, css.publishBtn)}
                onClick={handlePublish}
                disabled={saving}
              >
                {saving ? 'Updating...' : 'Update Live'}
              </button>
            </div>
          )}

          {saving && (
            <div className={css.loadingOverlay}>
              <div className={css.loadingContent}>
                <div className={css.spinner}></div>
                <div className={css.loadingText}>Saving changes...</div>
              </div>
            </div>
          )}

          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            title={confirmModal.title}
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const FortehBlogPageNew = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(FortehBlogPageNewComponent);

export default FortehBlogPageNew;
